/**
 * Хранилище очереди Штаба на устройстве (этап 4, шаг 2).
 *
 * Тонкая обёртка над IndexedDB без зависимостей. idb-keyval не подошёл: он
 * хранит пары ключ-значение без индексов и без чтения-изменения-записи в одной
 * транзакции, а очереди нужны выборка по пользователю, сквозной порядок записи
 * (счётчик в той же транзакции, что и вопрос) и аренда отправки для браузеров
 * без Web Locks. На это хватает сотни строк.
 *
 * Голос хранится как ArrayBuffer + mime, а не Blob: Blob в IndexedDB старые
 * WebKit не сохраняли, ArrayBuffer сохраняется везде.
 *
 * IndexedDB может не открыться (приватный режим части браузеров, запрет
 * хранилища): тогда очередь живёт в памяти до закрытия приложения и честно
 * говорит об этом через persistent() - экран не обещает «переживёт закрытие».
 */

export type QueuedKind = 'text' | 'voice'

/**
 * waiting - ждёт отправки (сети, своей очереди или повтора после сбоя связи);
 * unrecognized - голос не распознан, ждёт решения человека;
 * limit - упёрлись в правило тарифа (лимит Штаба, подписка), повтор не поможет;
 * failed - сервер не принимает вопрос (направление недоступно, повторные сбои).
 */
export type QueuedState = 'waiting' | 'unrecognized' | 'limit' | 'failed'

export interface QueuedQuestion {
  /** client_message_id: с ним уходит вопрос и каждый повтор. */
  id: string
  /** Владелец: чужой пользователь на устройстве очередь не видит и не отправляет. */
  userId: string
  /** Направление, в котором задан вопрос: уходит туда же, даже если тренер переключился. */
  assistantId: string
  /** Сквозной порядок записи на устройстве. */
  seq: number
  createdAt: string
  kind: QueuedKind
  /**
   * Текст вопроса как он уйдёт на сервер (с контекстом экрана). У голоса -
   * после распознавания: второй раз запись не распознаём.
   */
  text?: string
  /** Голос: контекст экрана, добавится к распознанному тексту. */
  contextLabel?: string
  /** Голос до распознавания. После распознавания стирается. */
  audio?: ArrayBuffer
  mime?: string
  durationMs?: number
  state: QueuedState
  /** limit: код правила ('ai_limit_exceeded', 'soft_locked'). */
  reason?: string
  /** Подряд неудачных попыток из-за сбоя сервера (не связи). */
  serverFailures: number
  /** Последняя попытка сорвалась по связи или серверу: «Не отправлено». */
  lastErrorAt?: string
}

export type NewQuestion = Omit<QueuedQuestion, 'seq' | 'state' | 'serverFailures'>

export type QuestionPatch = Partial<Omit<QueuedQuestion, 'id' | 'userId' | 'seq'>>

export interface QueueStore {
  /** Вопросы пользователя в порядке записи. */
  list(userId: string): Promise<QueuedQuestion[]>
  get(id: string): Promise<QueuedQuestion | null>
  add(q: NewQuestion): Promise<QueuedQuestion>
  /** Поле со значением undefined удаляется. Нет записи - null. */
  patch(id: string, patch: QuestionPatch): Promise<QueuedQuestion | null>
  remove(id: string): Promise<void>
  /** Аренда отправки для браузеров без Web Locks: true - аренда наша. */
  tryLease(owner: string, ttlMs: number, nowMs: number): Promise<boolean>
  releaseLease(owner: string): Promise<void>
  /** Переживёт ли очередь закрытие приложения. */
  persistent(): boolean
}

function applyPatch(cur: QueuedQuestion, patch: QuestionPatch): QueuedQuestion {
  const next = { ...cur, ...patch } as Record<string, unknown>
  for (const [k, v] of Object.entries(patch)) if (v === undefined) delete next[k]
  return next as unknown as QueuedQuestion
}

const bySeq = (a: QueuedQuestion, b: QueuedQuestion) => a.seq - b.seq

/** Очередь в памяти: стенд, тесты и запасной путь без IndexedDB. */
export function createMemoryQueueStore(): QueueStore {
  const items = new Map<string, QueuedQuestion>()
  let seq = 0
  let lease: { owner: string; until: number } | null = null
  const copy = (q: QueuedQuestion) => ({ ...q })
  return {
    async list(userId) {
      return [...items.values()].filter((q) => q.userId === userId).sort(bySeq).map(copy)
    },
    async get(id) {
      const q = items.get(id)
      return q ? copy(q) : null
    },
    async add(q) {
      const full: QueuedQuestion = { ...q, seq: ++seq, state: 'waiting', serverFailures: 0 }
      items.set(full.id, full)
      return copy(full)
    },
    async patch(id, patch) {
      const cur = items.get(id)
      if (!cur) return null
      const next = applyPatch(cur, patch)
      items.set(id, next)
      return copy(next)
    },
    async remove(id) {
      items.delete(id)
    },
    async tryLease(owner, ttlMs, nowMs) {
      if (lease && lease.owner !== owner && lease.until > nowMs) return false
      lease = { owner, until: nowMs + ttlMs }
      return true
    },
    async releaseLease(owner) {
      if (lease?.owner === owner) lease = null
    },
    persistent: () => false,
  }
}

const DB_NAME = 'procoach-hub-queue'
const DB_VERSION = 1
const QUESTIONS = 'questions'
const META = 'meta'

export function openQueueDb(factory: IDBFactory | undefined = globalThis.indexedDB, name = DB_NAME): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!factory) {
      reject(new Error('indexedDB unavailable'))
      return
    }
    let r: IDBOpenDBRequest
    try {
      r = factory.open(name, DB_VERSION)
    } catch (err) {
      reject(err)
      return
    }
    r.onupgradeneeded = () => {
      const db = r.result
      if (!db.objectStoreNames.contains(QUESTIONS)) {
        db.createObjectStore(QUESTIONS, { keyPath: 'id' }).createIndex('by_user', 'userId')
      }
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META)
    }
    r.onsuccess = () => {
      const db = r.result
      // Новая версия приложения обновляет схему в другой вкладке - не держим её
      db.onversionchange = () => db.close()
      resolve(db)
    }
    r.onerror = () => reject(r.error ?? new Error('indexedDB open failed'))
  })
}

/**
 * Транзакция без await внутри: запросы на колбэках, итог - по oncomplete.
 * Так транзакция не закроется раньше времени ни в одном браузере.
 */
function run<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  body: (tx: IDBTransaction, set: (v: T) => void) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction([QUESTIONS, META], mode)
    let value: T
    tx.oncomplete = () => resolve(value)
    tx.onerror = () => reject(tx.error ?? new Error('indexedDB transaction failed'))
    tx.onabort = () => reject(tx.error ?? new Error('indexedDB transaction aborted'))
    body(tx, (v) => {
      value = v
    })
  })
}

export function createIdbQueueStore(db: IDBDatabase): QueueStore {
  return {
    list: (userId) =>
      run<QueuedQuestion[]>(db, 'readonly', (tx, set) => {
        const r = tx.objectStore(QUESTIONS).index('by_user').getAll(userId)
        r.onsuccess = () => set((r.result as QueuedQuestion[]).sort(bySeq))
      }),
    get: (id) =>
      run<QueuedQuestion | null>(db, 'readonly', (tx, set) => {
        const r = tx.objectStore(QUESTIONS).get(id)
        r.onsuccess = () => set((r.result as QueuedQuestion | undefined) ?? null)
      }),
    add: (q) =>
      run<QueuedQuestion>(db, 'readwrite', (tx, set) => {
        const meta = tx.objectStore(META)
        const r = meta.get('seq')
        r.onsuccess = () => {
          const seq = (typeof r.result === 'number' ? r.result : 0) + 1
          meta.put(seq, 'seq')
          const full: QueuedQuestion = { ...q, seq, state: 'waiting', serverFailures: 0 }
          tx.objectStore(QUESTIONS).put(full)
          set(full)
        }
      }),
    patch: (id, patch) =>
      run<QueuedQuestion | null>(db, 'readwrite', (tx, set) => {
        const store = tx.objectStore(QUESTIONS)
        const r = store.get(id)
        r.onsuccess = () => {
          const cur = r.result as QueuedQuestion | undefined
          if (!cur) {
            set(null)
            return
          }
          const next = applyPatch(cur, patch)
          store.put(next)
          set(next)
        }
      }),
    remove: (id) =>
      run<void>(db, 'readwrite', (tx) => {
        tx.objectStore(QUESTIONS).delete(id)
      }),
    tryLease: (owner, ttlMs, nowMs) =>
      run<boolean>(db, 'readwrite', (tx, set) => {
        const meta = tx.objectStore(META)
        const r = meta.get('lease')
        r.onsuccess = () => {
          const cur = r.result as { owner: string; until: number } | undefined
          if (cur && cur.owner !== owner && cur.until > nowMs) {
            set(false)
            return
          }
          meta.put({ owner, until: nowMs + ttlMs }, 'lease')
          set(true)
        }
      }),
    releaseLease: (owner) =>
      run<void>(db, 'readwrite', (tx) => {
        const meta = tx.objectStore(META)
        const r = meta.get('lease')
        r.onsuccess = () => {
          const cur = r.result as { owner: string } | undefined
          if (cur?.owner === owner) meta.delete('lease')
        }
      }),
    persistent: () => true,
  }
}

/**
 * Очередь устройства: IndexedDB, а если он не открылся - память. Открывается
 * лениво, при первом обращении.
 */
export function createDeviceQueueStore(open: () => Promise<IDBDatabase> = () => openQueueDb()): QueueStore {
  let ready: Promise<QueueStore> | null = null
  let current: QueueStore | null = null
  const store = () => {
    ready ??= open()
      .then((db) => createIdbQueueStore(db))
      .catch(() => createMemoryQueueStore())
      .then((s) => (current = s))
    return ready
  }
  return {
    list: async (u) => (await store()).list(u),
    get: async (id) => (await store()).get(id),
    add: async (q) => (await store()).add(q),
    patch: async (id, p) => (await store()).patch(id, p),
    remove: async (id) => (await store()).remove(id),
    tryLease: async (o, t, n) => (await store()).tryLease(o, t, n),
    releaseLease: async (o) => (await store()).releaseLease(o),
    // До первого обращения считаем, что сохранится: IndexedDB есть почти везде
    persistent: () => current?.persistent() ?? true,
  }
}

export async function blobToBuffer(b: Blob): Promise<ArrayBuffer> {
  if (typeof b.arrayBuffer === 'function') return b.arrayBuffer()
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result as ArrayBuffer)
    r.onerror = () => reject(r.error ?? new Error('read failed'))
    r.readAsArrayBuffer(b)
  })
}
